import React, { useId } from 'react';
import { Message } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import Icon from '../../components/ui-components/Icon';
import { ChatSessionKey } from '../../types/EntityKeys';
import FromAvatar from './FromAvatar';
import MessageAttachment from './MessageAttachment';
import MessageSender, { getMessageSenderName } from './MessageSender';
import { getMessageSentAriaLabel } from 'flow/utils/message-row-utils';
import MessageOpenGraphItem from 'flow/pages/messages/MessageOpenGraphItem';
import { twMerge } from 'tailwind-merge';
import Reactions from './reactions/Reactions';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectMessageReactions } from 'flow/selectors/chat-message-selectors';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import ThreadMessageSummary from './thread-message/ThreadSummary';

import useContact from 'flow/hooks/useContact';
import useAppContext from 'flow/hooks/useAppContext';
import ChatMessageActionBar from './ChatMessageActionBar';
import ChatMessageQuote from './ChatMessageQuote';
import ChatMessageMeta from './ChatMessageMeta';
import { useThreadView } from 'flow/contexts/MessageThreadCheckProvider';
import StyledMessageBody from 'flow/components/StyledMessageBody';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
    cbOpenGraph?: () => void;
    showAvatar: boolean;
    showReactions?: boolean;
    showContextMenu?: boolean;
    showThreadMessageSummary?: boolean;
    messageLineClamp?: number;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const ChatMessage = React.memo(
    ({
        message,
        sessionKey,
        cbOpenGraph,
        showAvatar,
        showReactions = true,
        showContextMenu = true,
        showThreadMessageSummary = true,
        messageLineClamp = null,
        videoDark = false,
    }: Props) => {
        const { hasFeature } = useFeatureFlags();
        const isThreadView = useThreadView();
        const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
        const { ownContact } = useContact();
        const { i18next } = useAppContext();

        // What a screen reader announces when the row takes focus: who and
        // when as the name, the text as the description. The list around
        // the rows raises one of them to tabIndex 0 (useRovingMessageList).
        const senderName = getMessageSenderName(
            message,
            ownExtensionKey,
            i18next,
        );
        const sentLabel = getMessageSentAriaLabel(message.get('sent'));
        // From useId, not the message key: the same message renders again
        // as a thread's parent while the thread is open.
        const bodyId = `${useId()}-body`;
        const currentThread = useAppSelector((state) =>
            state.chats.getIn(['threadMessage', 'currentThread']),
        );

        const {
            chatSessionKey: selectedChatSessionKey,
            chatMessageKey: selectedChatMessageKey,
        } = currentThread?.toJS() || {};
        const threadParentKey = isThreadView ? selectedChatMessageKey : null;

        const reactions = useAppSelector((state) =>
            selectMessageReactions(state, sessionKey, message.get('key')),
        );

        const myReactions = reactions
            .filter((reaction) =>
                reaction.get('users').includes(ownExtensionKey),
            )
            .map((reaction) => reaction.get('type'))
            .toArray();

        const fromExtension =
            !message.getIn(['from', 'contact']) && message.get('failed', false)
                ? message.setIn(['from', 'contact'], ownContact).get('from')
                : message.get('from');

        const threadSummary =
            message.get('type') !== 'post' && !!hasFeature(FeatureFlags.THREADS)
                ? message.get('threadSummary')
                : null;
        return (
            <article
                data-message-row=""
                tabIndex={-1}
                aria-label={`${senderName}, ${sentLabel}`}
                aria-describedby={bodyId}
                className={twMerge(
                    'group focus-ring-inset relative flex w-full scroll-mt-10 items-start gap-x-2 rounded-sm pe-4',
                    showAvatar ? 'ps-1 pt-4' : 'ps-15',
                )}
            >
                {showAvatar && (
                    <div className="ms-2 mt-3">
                        <FromAvatar
                            from={fromExtension}
                            chatSessionKey={sessionKey}
                        />
                    </div>
                )}

                <div
                    className={twMerge(
                        'w-full rounded-sm p-2',
                        videoDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100',
                    )}
                >
                    <ChatMessageActionBar
                        message={message}
                        sessionKey={sessionKey}
                        showAvatar={showAvatar}
                        showReactions={showReactions}
                        showContextMenu={showContextMenu}
                        myReactions={myReactions}
                        threadSummary={threadSummary}
                        threadParentKey={threadParentKey}
                        videoDark={videoDark}
                    />

                    <div className="flex w-full justify-between gap-x-2">
                        <div className="flex flex-col justify-between gap-y-2">
                            {showAvatar && (
                                <MessageSender
                                    message={message}
                                    sessionKey={sessionKey}
                                />
                            )}
                            <div className="flex w-full flex-col gap-y-1">
                                <ChatMessageQuote
                                    message={message}
                                    sessionKey={sessionKey}
                                    videoDark={videoDark}
                                />

                                <div className="flex gap-2">
                                    {message.getIn([
                                        'quotedMessage',
                                        'quotedMessageKey',
                                    ]) ? (
                                        <Icon
                                            iconKey="turnLeft"
                                            className={twMerge(
                                                'rotate-180 text-xs',
                                                videoDark
                                                    ? 'text-gray-500'
                                                    : 'text-gray-300',
                                            )}
                                        />
                                    ) : null}

                                    <div
                                        id={bodyId}
                                        dir="auto"
                                        className={twMerge(
                                            'leading-6 wrap-anywhere whitespace-pre-wrap',
                                            videoDark ? 'text-gray-300' : '',
                                            messageLineClamp
                                                ? `line-clamp-${messageLineClamp}`
                                                : '',
                                        )}
                                    >
                                        <StyledMessageBody
                                            message={message}
                                            sessionKey={sessionKey}
                                            videoDark={videoDark}
                                        />
                                    </div>
                                </div>

                                <MessageOpenGraphItem
                                    message={message}
                                    cbOpenGraph={cbOpenGraph}
                                />
                                <MessageAttachment
                                    message={message}
                                    sessionKey={sessionKey}
                                    videoDark={videoDark}
                                />
                            </div>
                        </div>

                        <ChatMessageMeta
                            message={message}
                            sessionKey={sessionKey}
                            videoDark={videoDark}
                        />
                    </div>
                    {showThreadMessageSummary && (
                        <ThreadMessageSummary
                            threadSummary={threadSummary?.toJS()}
                            isThreadOpened={
                                selectedChatSessionKey === sessionKey &&
                                selectedChatMessageKey === message.get('key')
                            }
                            keys={{
                                chatSessionKey: sessionKey,
                                chatMessageKey: message.get('key'),
                            }}
                        />
                    )}

                    {hasFeature(FeatureFlags.REACTIONS) && (
                        <Reactions
                            data={reactions.size > 0 ? reactions.toJS() : null}
                            keys={{
                                chatSessionKey: sessionKey,
                                chatMessageKey: message.get('key'),
                                threadParentKey,
                            }}
                        />
                    )}
                </div>
            </article>
        );
    },
    (prev, next) =>
        prev.message === next.message &&
        prev.sessionKey === next.sessionKey &&
        prev.showAvatar === next.showAvatar &&
        prev.showContextMenu === next.showContextMenu &&
        prev.showReactions === next.showReactions &&
        prev.showThreadMessageSummary === next.showThreadMessageSummary &&
        prev.messageLineClamp === next.messageLineClamp,
);

// Without this the memo wraps an anonymous arrow, and every message row shows
// up as "Anonymous" in the React profiler - which is where you go to find out
// why they rendered.
ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
